import * as React from 'react';
import { Droppable, Draggable, DroppableProps } from 'react-beautiful-dnd';
import { ArrowUpDown, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Deal, DealStage, DealStageData } from '../../types/pipeline.types';
import { DealCard } from './DealCard';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// React 18 StrictMode compatibility fix for react-beautiful-dnd
function StrictModeDroppable({ children, ...props }: DroppableProps) {
  const [enabled, setEnabled] = React.useState(false);

  React.useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) return null;

  return <Droppable {...props}>{children}</Droppable>;
}

interface KanbanColumnProps {
  stage: DealStage;
  data: DealStageData;
  onDealClick?: (deal: Deal) => void;
  onEditDeal?: (dealId: string) => void;
  onDeleteDeal?: (dealId: string, stageId: string) => void;
  onSortChange?: (stageId: string, order: 'ASC' | 'DESC') => void;
  onLoadMore?: (stageId: string) => void;
}

const STAGE_COLORS: Record<
  number,
  { bg: string; border: string; dot: string }
> = {
  1: {
    bg: 'bg-slate-50 dark:bg-slate-900/50',
    border: 'border-slate-300 dark:border-slate-700',
    dot: 'bg-slate-400',
  },
  2: {
    bg: 'bg-primary/5 dark:bg-primary/10',
    border: 'border-primary/60',
    dot: 'bg-primary',
  },
  3: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-400 dark:border-amber-600',
    dot: 'bg-amber-500',
  },
  4: {
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    border: 'border-cyan-400 dark:border-cyan-600',
    dot: 'bg-cyan-500',
  },
  5: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-400 dark:border-emerald-600',
    dot: 'bg-emerald-500',
  },
  6: {
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    border: 'border-violet-400 dark:border-violet-600',
    dot: 'bg-violet-500',
  },
};

function getStageColors(sequence: number) {
  const idx = ((sequence - 1) % 6) + 1;
  return STAGE_COLORS[idx] || STAGE_COLORS[1];
}

export function KanbanColumn({
  stage,
  data,
  onDealClick,
  onEditDeal,
  onDeleteDeal,
  onSortChange,
  onLoadMore,
}: KanbanColumnProps) {
  const colors = getStageColors(stage.sequence);
  const dealCount = data.deals.length;

  const renderHeader = () => (
    <div
      className={cn(
        'flex items-center justify-between p-3 rounded-t-lg',
        colors.bg
      )}
    >
      <div className="flex items-center gap-2">
        <div className={cn('size-2 rounded-full', colors.dot)} />
        <span className="font-medium text-sm text-foreground">
          {stage.label}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground bg-background px-2 py-0.5 rounded">
          {dealCount}
          {data.canLoadMore ? '+' : ''}
        </span>
        {dealCount > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="size-6 p-0">
                <ArrowUpDown className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className={data.sortOrder === 'DESC' ? 'text-primary' : ''}
                onClick={() => onSortChange?.(stage.id, 'DESC')}
              >
                Newest first
              </DropdownMenuItem>
              <DropdownMenuItem
                className={data.sortOrder === 'ASC' ? 'text-primary' : ''}
                onClick={() => onSortChange?.(stage.id, 'ASC')}
              >
                Oldest first
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );

  const renderEmpty = () => (
    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
      No deals in this stage
    </div>
  );

  const renderLoading = () => (
    <div className="flex items-center justify-center h-32">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  );

  const renderLoadMore = () => {
    if (!data.canLoadMore) return null;
    return (
      <Button
        variant="secondary"
        size="sm"
        className="w-full mt-2"
        onClick={() => onLoadMore?.(stage.id)}
        disabled={data.isLoading}
      >
        {data.isLoading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          'Load More'
        )}
      </Button>
    );
  };

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border-2 border-dashed min-w-[320px] w-[320px] lg:min-w-[360px] lg:w-[360px]',
        colors.border
      )}
    >
      {renderHeader()}

      <StrictModeDroppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              'flex-1 p-2 space-y-3 min-h-[300px] max-h-[calc(100vh-280px)] overflow-y-auto transition-colors',
              snapshot.isDraggingOver && 'bg-primary/5'
            )}
          >
            {data.isLoading && data.deals.length === 0
              ? renderLoading()
              : data.deals.length === 0
              ? renderEmpty()
              : data.deals.map((deal, index) => (
                  <Draggable key={deal.id} draggableId={deal.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        <DealCard
                          deal={deal}
                          isDragging={dragSnapshot.isDragging}
                          onClick={onDealClick}
                          onEdit={onEditDeal}
                          onDelete={
                            onDeleteDeal
                              ? (id) => onDeleteDeal(id, stage.id)
                              : undefined
                          }
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
            {provided.placeholder}
            {renderLoadMore()}
          </div>
        )}
      </StrictModeDroppable>
    </div>
  );
}
